import React, { useCallback, useEffect } from "react"
import { Modal, Steps, Button, Typography } from "antd"
import {
  RobotOutlined,
  FormOutlined,
  EditOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons"
import type { RadioChangeEvent } from "antd"
import { customPromptTemplate,PREDEFINED_PROMPTS } from "common/Utility"
import { useTagGeneration,useLoadingMessages,useWizardSteps,useErrorHandling } from "hooks/WizardHooks" 
import { SelectPromptStep } from "components/molecules/WizardSelectPromptStep"
import { RefinePromptStep } from "components/molecules/WizardRefinePromptStep"
import { GenerateStep } from "components/molecules/WizardGenerateStep"
import { ReviewStep } from "components/molecules/WizardReviewStep"
import { SaveStep } from "components/molecules/WizardSaveStep"
import { ErrorAlert } from "components/molecules/WizardErrorAlert"
import type { AITagGenerationWizardProps } from "types/components/IAITagGenerationWizard"
const { Step } = Steps
const { Text } = Typography

const AITagGenerationWizard: React.FC<AITagGenerationWizardProps> = ({
  visible,
  onClose,
  generateTagsWithAI,
  createTags,
  isGeneratingTags,
  isCreatingTags,
  businessAreaList,
  aiGeneratedTags,
  resetAiGeneratedTags,
  aiGeneratedErrors,
  bulkCreateErrors,
  DataTagList,
  rephraseWithAI,
  isRephrasing,
  rephrasedDataTag,
}) => {
  const {
    generatedTags,
    requestedTagCount,
    setRequestedTagCount,
    customPrompt,
    setCustomPrompt,
    selectedPromptId,
    setSelectedPromptId,
    createCustomPrompt,
    setCreateCustomPrompt,
    searchText,
    setSearchText,
    viewMode,
    setViewMode,
    handleGenerateTags,
    handleTagSelection,
    handleSelectAll,
    handleDeselectAll,
    getFilteredTags,
    resetTagGeneration,
    handleRephraseDescription,
    handleRevertDescription,
    handleUpdateDescription,
    currentRephrasingTagIndex
  } = useTagGeneration(
    generateTagsWithAI,
    resetAiGeneratedTags,
    aiGeneratedTags,
    DataTagList,
    isGeneratingTags,
    aiGeneratedErrors,
    rephraseWithAI,
    isRephrasing,
    rephrasedDataTag,
  )

  const {
    currentStep,
    setCurrentStep,
    saveInitiated,
    setSaveInitiated,
    hasUnsavedChanges,
    lastErrorMessage,
    nextStep,
    prevStep,
    resetSteps,
  } = useWizardSteps(isGeneratingTags, isCreatingTags, generatedTags, aiGeneratedErrors, bulkCreateErrors)

  const { currentLoadingMessage, currentConfirmationMessage } = useLoadingMessages(isGeneratingTags, isCreatingTags)
  const { errorObj } = useErrorHandling(aiGeneratedErrors ?? null, bulkCreateErrors ?? null);

  const [selectedBusinessArea, setSelectedBusinessArea] = React.useState<string>(
    businessAreaList.find((area) => area.name === "DEFAULT")?.businessAreaId ||
      (businessAreaList.length > 0 ? businessAreaList[0].businessAreaId : ""),
  )

  const handlePromptChange = (e: RadioChangeEvent) => {
    if (e.target.value === "custom") {
      setCreateCustomPrompt(true)
      setSelectedPromptId(null)
      setCustomPrompt(customPromptTemplate)
    } else {
      setCreateCustomPrompt(false)
      setSelectedPromptId(e.target.value)
      const selectedPrompt = PREDEFINED_PROMPTS.find((p) => p.id === e.target.value)
      if (selectedPrompt) {
        setCustomPrompt(selectedPrompt.prompt)
      }
    }
  }

  const handleCustomPromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCustomPrompt(e.target.value)
  }

  const handleBusinessAreaChange = (value: string) => {
    setSelectedBusinessArea(value)
  }

  const handleNextStep = () => {
    if (currentStep === 1) {
      handleGenerateTags()
      setCurrentStep(2) 
    } else {
      nextStep()
    }
  }

  const handleCancelGeneration = useCallback(() => {
    if (resetAiGeneratedTags) {
      resetAiGeneratedTags()
    }
    setCurrentStep(1) 
  }, [resetAiGeneratedTags, setCurrentStep])

  const handleConfirmTags = async () => {
    const selectedBusinessAreaName =
      businessAreaList.find((area) => area.businessAreaId === selectedBusinessArea)?.name || "DEFAULT"
    try {
      const selectedTags = generatedTags
        .filter((tag) => tag.selected)
        .map((tag) => ({
          tagName: tag.name,
          tagDescription: tag.description,
          businessAreaName: selectedBusinessAreaName,
        }))
      const payload = selectedTags.map((tag) => ({
        tagName: tag.tagName,
        tagDescription: tag.tagDescription,
        businessAreaId: selectedBusinessArea,
      }))
      if (createTags && payload.length > 0) {
        setSaveInitiated(true)
        setCurrentStep(4) 
        await createTags(payload)
      }
    } catch (error) {
      setCurrentStep(3)
      console.error("Error confirming tags:", error)
    }
  }

  const resetWizard = useCallback(() => {
    resetSteps()
    resetTagGeneration()
    setSelectedBusinessArea(
      businessAreaList.find((area) => area.name === "DEFAULT")?.businessAreaId ||
        (businessAreaList.length > 0 ? businessAreaList[0].businessAreaId : ""),
    )
  }, [resetSteps, resetTagGeneration, businessAreaList])

  useEffect(() => {
    if (currentStep === 4 && saveInitiated && !isCreatingTags && !bulkCreateErrors) {
      const timer = setTimeout(() => {
        resetWizard()
        onClose()
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [currentStep, saveInitiated, isCreatingTags, bulkCreateErrors, resetWizard, onClose])

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges && !isGeneratingTags && !isCreatingTags) {
      Modal.confirm({
        title: "Discard changes?",
        icon: <ExclamationCircleOutlined />,
        content: "You have unsaved changes. Are you sure you want to close the wizard?",
        okText: "Yes, discard",
        cancelText: "No, continue editing",
        onOk() {
          resetWizard()
          onClose()
        },
      })
    } else {
      resetWizard()
      onClose()
    }
  }, [hasUnsavedChanges, isGeneratingTags, isCreatingTags, resetWizard, onClose])

  const steps = [
    {
      title: "Select Prompt",
      icon: <FormOutlined />,
      content: (
        <SelectPromptStep
          selectedPromptId={selectedPromptId}
          createCustomPrompt={createCustomPrompt}
          selectedBusinessArea={selectedBusinessArea}
          requestedTagCount={requestedTagCount}
          businessAreaList={businessAreaList}
          setSelectedPromptId={setSelectedPromptId}
          setCreateCustomPrompt={setCreateCustomPrompt}
          setCustomPrompt={setCustomPrompt}
          setRequestedTagCount={setRequestedTagCount}
          handleBusinessAreaChange={handleBusinessAreaChange}
          handlePromptChange={handlePromptChange}
        />
      ),
    },
    {
      title: "Refine Prompt",
      icon: <EditOutlined />,
      content: <RefinePromptStep customPrompt={customPrompt} handleCustomPromptChange={handleCustomPromptChange} />,
    },
    {
      title: "Generate",
      icon: <ThunderboltOutlined />,
      content: <GenerateStep currentLoadingMessage={currentLoadingMessage} onCancel={handleCancelGeneration} />,
    },
    {
      title: "Review",
      icon: <BulbOutlined />,
      content: (
        <ReviewStep
          generatedTags={generatedTags}
          selectedBusinessArea={selectedBusinessArea}
          businessAreaList={businessAreaList}
          searchText={searchText}
          viewMode={viewMode}
          handleBusinessAreaChange={handleBusinessAreaChange}
          handleTagSelection={handleTagSelection}
          handleSelectAll={handleSelectAll}
          handleDeselectAll={handleDeselectAll}
          setSearchText={setSearchText}
          setViewMode={setViewMode}
          handleRephraseDescription={handleRephraseDescription}
          handleRevertDescription={handleRevertDescription}
          handleUpdateDescription={handleUpdateDescription}
          isRephrasing={isRephrasing}
          currentRephrasingTagIndex={currentRephrasingTagIndex}
        />
      ),
    },
    {
      title: "Save",
      icon: <SaveOutlined />,
      content: (
        <SaveStep
          isCreatingTags={isCreatingTags}
          currentConfirmationMessage={currentConfirmationMessage}
          selectedTagCount={generatedTags.filter((t) => t.selected).length}
          totalTagCount={generatedTags.length}
        />
      ),
    },
  ]
  return (
    <Modal
      title={
        <div className="flex items-center">
          <RobotOutlined className="text-blue-500 mr-2 text-xl" />
          <span className="font-bold text-xl">AI-Assisted Tag Generation Wizard</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      closeIcon={<CloseCircleOutlined className="text-3xl" />}
      width={800}
      maskClosable={false}
      centered
      footer={null}
      className="ai-tag-wizard"
      destroyOnClose={true}
    >
      <ErrorAlert
        activeError={errorObj}
        lastErrorMessage={lastErrorMessage}
      />

      <Steps current={currentStep} className="mb-8 mt-4">
        {steps.map((step) => (
          <Step key={step.title} title={step.title} icon={step.icon} />
        ))}
      </Steps>

      <div className="p-4 min-h-[320px] border border-dashed border-gray-300 rounded-lg bg-gray-50">
        {steps[currentStep].content}
      </div>

      {currentStep === 4 && !isCreatingTags && !bulkCreateErrors && saveInitiated && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
          <Text className="text-green-600">
            <CheckCircleOutlined className="mr-2" />
            Tags saved successfully! This window will close automatically.
          </Text>
        </div>
      )}

      <div className="mt-8 flex justify-between">
        {currentStep > 0 && !isGeneratingTags && !isCreatingTags && <Button onClick={prevStep}>Previous</Button>}
        {currentStep === 0 && <Button onClick={handleCancel}>Cancel</Button>}
        {isGeneratingTags || isCreatingTags ? <div></div> : null}

        <div>
          {currentStep < 2 && (
            <Button
              type="primary"
              onClick={handleNextStep}
              disabled={
                (currentStep === 0 && !selectedPromptId && !createCustomPrompt) ||
                (currentStep === 1 && !customPrompt.trim())
              }
              className="bg-gradient-to-r from-blue-500 to-purple-500 border-none hover:from-blue-600 hover:to-purple-600"
            >
              Next
            </Button>
          )}
          {currentStep === 3 && (
            <Button
              type="primary"
              onClick={handleNextStep}
              disabled={generatedTags.filter((t) => t.selected).length === 0}
              className="bg-gradient-to-r from-blue-500 to-purple-500 border-none hover:from-blue-600 hover:to-purple-600"
            >
              Next
            </Button>
          )}
          {currentStep === 4 && (
            <Button
              type="primary"
              onClick={handleConfirmTags}
              disabled={generatedTags.filter((t) => t.selected).length === 0}
              className="bg-gradient-to-r from-blue-500 to-purple-500 border-none hover:from-blue-600 hover:to-purple-600"
            >
              Save Tags
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default AITagGenerationWizard
